import { useEffect, useRef } from "react";
import gsap from "gsap";
import { CLAY_COLORS, MAX_RPM, type ClayColorId } from "./constants";

interface WheelPanelProps {
  speedPct: number;
  onSpeedPct: (v: number) => void;
  color: ClayColorId;
  onColor: (id: ClayColorId) => void;
}

export function WheelPanel({ speedPct, onSpeedPct, color, onColor }: WheelPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rootRef.current) return;
    gsap.fromTo(
      rootRef.current,
      { x: 24, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.9, ease: "power3.out", delay: 0.4 },
    );
  }, []);

  const rpm = Math.round((speedPct / 100) * MAX_RPM);

  const triggerRipple = () => {
    if (!ringRef.current) return;
    gsap.fromTo(
      ringRef.current,
      { scale: 0.2, opacity: 0.55 },
      { scale: 1.8, opacity: 0, duration: 0.6, ease: "power2.out" },
    );
  };

  return (
    <div ref={rootRef} className="pointer-events-auto fixed top-1/2 right-4 md:right-8 -translate-y-1/2 z-30">
      <div
        className="relative border border-background-700 bg-background-950/85 px-2 py-3 w-[188px] md:w-[208px] overflow-hidden"
        style={{ borderColor: "#3A3532" }}
      >
        <div
          ref={ringRef}
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at center, oklch(var(--accent-500) / 0.15), transparent 60%)", opacity: 0 }}
        />

        <div className="px-2 pb-3 mb-2 border-b border-background-800 relative">
          <p className="font-mono-caps text-[10px] text-foreground-400">Wheel</p>
        </div>

        <div className="px-2 relative">
          <div className="flex items-baseline justify-between mb-2">
            <p className="font-mono-caps text-[10px] text-foreground-400">Speed</p>
            <p className="font-mono-caps text-[10px] text-accent-500 tabular-nums">{rpm} RPM</p>
          </div>
          <input
            className="pottery-range"
            type="range"
            min={0}
            max={100}
            step={1}
            value={speedPct}
            onChange={(e) => onSpeedPct(parseInt(e.target.value, 10))}
            aria-label={`Wheel speed ${rpm} RPM`}
          />
          <p className="font-mono-caps text-[9px] text-foreground-500 mt-1">Press [ / ] to adjust</p>
        </div>

        <div className="mt-4 pt-3 border-t border-background-800 px-2 relative">
          <p className="font-mono-caps text-[10px] text-foreground-400 mb-3">Clay</p>
          <div className="flex items-center justify-between">
            {CLAY_COLORS.map((c) => {
              const active = c.id === color;
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-label={c.label}
                  aria-pressed={active}
                  onClick={() => {
                    onColor(c.id);
                    triggerRipple();
                  }}
                  className="relative w-7 h-7 flex items-center justify-center cursor-pointer"
                >
                  <span
                    className={`block w-6 h-6 rounded-full transition-transform ${
                      active ? "scale-110" : "hover:scale-105"
                    }`}
                    style={{
                      background: c.hex,
                      boxShadow: active
                        ? "0 0 0 1px oklch(var(--accent-500)), inset 0 0 0 1px rgba(255,255,255,0.08)"
                        : "inset 0 0 0 1px rgba(255,255,255,0.06)",
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}